import { useMemo, useState } from 'react';
import moment from 'moment';
import { useListEntitiesQuery } from '../../api/useListEntitiesQuery';
import { useNavigate } from '../../hooks/useNavigate';
import { useRouteParams } from '../../hooks/useRouteParams';
import { downloadFile } from '../../../CommonUtils/downloadFile';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { useDownloadFileMutation } from '../../api/useDownloadFileMutation';
import { AsyncComponentHookResult } from '../../../Types/ComponentHookResult';
import { useDocumentVaultContext } from '../../context/useDocumentVaultContext';
import { EntitiesDTO, EntityDTO, EntityTypeDTO } from '../../DocumentVault.meta';

export interface UseEntityListActions {
  onEntityPress(entity: EntityDTO): void;
  setCurrentVideoEntity(entity: EntityDTO | null): void;
}

export const useEntityList = (): AsyncComponentHookResult<EntitiesDTO, UseEntityListActions> & {
  isDownloadingFile: boolean;
  currentVideoEntity: EntityDTO | null;
} => {
  const [currentVideoEntity, setCurrentVideoEntity] = useState<EntityDTO | null>(null);

  const { clientId, path, providerType, filterableByYear } = useRouteParams();
  const queryResult = useListEntitiesQuery({ clientId, path, providerType, filterableByYear });

  const { mutateAsync: downloadFileBlob, isLoading: isDownloadingFile } = useDownloadFileMutation();

  const navigate = useNavigate();

  const { setSelectedEntity, setForceYear } = useDocumentVaultContext();

  const data = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    const { items, ...rest } = queryResult.data;

    return {
      ...rest,
      items: items.map(entity => ({
        ...entity,
        lastModified: entity.lastModified
          ? moment(entity.lastModified).format('MMM. D, YYYY')
          : null,
      })),
    };
  }, [queryResult.data]);

  const onEntityPress = async (entity: EntityDTO) => {
    switch (entity.type) {
      case EntityTypeDTO.File:
        try {
          const { fileName, fileBlob } = await downloadFileBlob({
            clientId,
            path: entity.path,
            externalFileId: entity.externalFileId,
            providerType: entity.providerType,
          });

          return downloadFile(fileName || entity.name, fileBlob);
        } catch {
          return toast.error('There was an error downloading this file. Please try again later.');
        }

      case EntityTypeDTO.Folder: {
        const canNavigate = setSelectedEntity(entity);

        if (!canNavigate) {
          return null;
        }

        if (entity.forceYear) {
          setForceYear(entity.forceYear);
        }

        return navigate({
          path: entity.path ? encodeURIComponent(entity.path) : '',
          providerType: entity.providerType,
          filterableByYear: entity.filterableByYear,
        });
      }
      case EntityTypeDTO.Video:
        return setCurrentVideoEntity(entity);

      default:
        return toast.error('The selected entity type is not supported.');
    }
  };

  return {
    ...queryResult,
    data,
    isDownloadingFile,
    currentVideoEntity,
    actions: {
      onEntityPress,
      setCurrentVideoEntity,
    },
  };
};
